import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { between, Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { styledImage } from '@helpers/styledImage';
import ButtonStyles, { DefaultButtonStyles, CommonButtonTextStyles, } from '@stories/Components/Buttons/Button/Button.styles';
import styled, { css } from 'styled-components';
const Container = styled.header `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--headerHeight);
  z-index: ${Layers.Nav};
  background-color: ${brand.global.white};
  border-bottom: 1px solid ${brand.secondary.grey.light};
  ${transition('height', '0.15s')};

  @media ${from(Device.TabletLarge)} {
    &::before {
      content: '';
      position: fixed;
      top: var(--headerHeight);
      left: 0;
      width: 100%;
      height: calc(100vh - var(--headerHeight));
      background-color: rgb(000 000 000 / 70%);
      opacity: 0;
      pointer-events: none;
      backdrop-filter: blur(5px);
      ${transition('opacity', '0.3s')};
    }

    &[data-overlay='true'] {
      &::before {
        opacity: 1;
        pointer-events: all;
      }
    }
  }
`;
const Inner = styled.div `
  ${siteWide}

  display: flex;
  place-items: center;
`;
const TopContents = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    display: block;
    background-color: ${brand.tertiary.nearlyWhite.light};

    ${Inner} {
      justify-content: flex-end;
    }
  }
`;
const MainContents = styled.div `
  ${Inner} {
    justify-content: space-between;

    @media ${from(Device.TabletLarge)} {
      gap: 10px;
    }
  }

  &[data-reduce-search='true'] {
    ${Inner} {
      @media ${from(Device.TabletLarge)} {
        gap: 22px;
      }
    }
  }

  @media ${until(Device.TabletLarge)} {
    display: flex;
    place-items: center;
    height: inherit;
  }
`;
const LogoWrapper = styled.a `
  flex-shrink: 0;
`;
const Logo = styledImage() `
  display: block;
  aspect-ratio: 226 / 44;
  max-height: 44px;
  object-fit: contain;
  object-position: center;

  @media ${until(Device.TabletLarge)} {
    max-height: 38px;
    margin-left: -10px;
  }

  @media ${between(Device.TabletLarge, Device.DesktopLarge)} {
    max-width: 192px;
  }
`;
const MobileButtons = styled.div `
  display: flex;
  place-items: center;
  gap: 20px;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const sharedButtonStyles = css `
  ${ButtonReset}
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${brand.primary.purple.base};
  min-width: 40px;

  svg {
    --svgSize: 24px;

    width: var(--svgSize);
    height: var(--svgSize);
  }

  span {
    font-size: 12px;
  }
`;
const SearchButton = styled.button.attrs({ type: 'button' }) `
  ${sharedButtonStyles}
`;
const MenuButton = styled.button.attrs({ type: 'button' }) `
  ${sharedButtonStyles}
`;
const SearchWrapper = styled.div `
  min-width: ${fluid(200, 360)};

  [data-reduce-search='true'] & {
    @media ${between(Device.TabletLarge, Device.DesktopLarge)} {
      min-width: ${fluid(200, 360, Device.DesktopSmall)};
    }
  }

  input[type='search'] {
    border-width: 1px;
    font-size: 16px;

    @media ${between(Device.TabletLarge, Device.DesktopLarge)} {
      font-size: ${fluid(14, 16, Device.TabletLarge)};
    }

    &:not(:focus-visible) {
      --borderColour: ${brand.secondary.grey.dark};
    }
  }

  @media ${until(Device.TabletLarge)} {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    padding: var(--sitePadding);
    opacity: 0;
    pointer-events: none;
    background: rgb(255 255 255 / 90%);
    backdrop-filter: blur(5px);
    ${transition('opacity', '0.3s')}

    &[data-mobile-overlay-visible='true'] {
      opacity: 1;
      pointer-events: all;
    }
  }
`;
const LoginModal = styled.div `
  display: none;
  padding: 40px 0;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${brand.global.white};
  opacity: 0;

  &[data-open-modal='true'] {
    @keyframes fadeInOpacity {
      0% {
        opacity: 0;
      }

      100% {
        opacity: 1;
      }
    }

    display: flex;
    animation: fadeInOpacity 0.5s ease-out;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;

    z-index: ${Layers.Modal};
  }
`;
const LoginModalInner = styled.div `
  ${siteWide};
  display: flex;
  align-items: center;
  position: relative;
`;
const CloseButton = styled.button `
  ${ButtonReset};
  position: absolute;
  top: 0;
  right: var(--sitePadding);
  width: 32px;
  height: 32px;

  svg {
    width: 24px;
    height: 24px;
    color: ${brand.primary.purple.base};
  }

  span {
    ${srOnly};
  }
`;
const ModalGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px 60px;
  width: 100%;
`;
const LoginCard = styled.div `
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background-color: ${brand.tertiary.nearlyWhite.light};
  border-radius: 5px;

  @media ${until(Device.TabletLarge)} {
    grid-column: span 2;
  }

  ${ButtonStyles.Container} {
    width: fit-content;
  }

  .external-login-button {
    ${DefaultButtonStyles}
    --buttonHeight: ${fluid(32, 40)};
    --buttonFontSize: ${fluid(12, 14)};
    span {
      ${CommonButtonTextStyles}
    }
  }
`;
const ContentArea = styled.div ``;
const CardTitle = styled.h2 `
  ${fonts.ubuntu.bold};
  margin-bottom: 0;
  font-size: 1.25rem;
  color: ${brand.primary.purple.base};
`;
const CardText = styled.p `
  margin-bottom: 0;
`;
const HeaderStyles = {
    Container,
    Inner,
    TopContents,
    MainContents,
    LogoWrapper,
    Logo,
    MobileButtons,
    SearchButton,
    MenuButton,
    SearchWrapper,
    LoginModal,
    LoginModalInner,
    CloseButton,
    ModalGrid,
    LoginCard,
    ContentArea,
    CardTitle,
    CardText,
};
export default HeaderStyles;
